import * as React from 'react'
import { Dimensions, Platform, TouchableOpacity, StyleSheet } from 'react-native'
import { View, Text } from '../../../components/Themed'
import ImageObject from '../../../components/Image/ImageObject'
import Colors from '../../../constants/Colors'
import GlobalStyle, {
	sTextSize,
	smallTextSize,
	LARGE_LIST_WIDTH,
	MED_LIST_WIDTH,
	SMALL_LIST_WIDTH,
	mediumTextSize,
	MED_WIDTH,
	MAX_CHECKOUT_WIDTH,
	useGlobalStyles,
} from '../../../constants/GlobalStyle'
import Formatter from '../../../helpers/Formatter'
import { Ionicons } from '@expo/vector-icons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useAppSelector } from '../../../state/hooks'
import {
	isRestaurantOpen,
	getHoursStatus,
	formatTimeWithoutPadding,
	epochToHours,
} from '../../../helpers/restaurantHours'
import { useEffect, useMemo, useState } from 'react'
import { minutesUntilClose } from '../Helpers/timeUntilClose'
import { useGetTimeHoursDifference } from '../../../hooks/useGetTimeDifference'
import {
	colors,
	Inline,
	Stack,
	Text as DSText,
	Spread,
	useBreakpoints,
} from '../../../components/DesignSystem'
import { useFirebaseAnalyticsContext } from '../../../context/FirebaseAnalyticsContext'

/**
 * Restaurant view on the home page of the app if showRestaurants is true
 */
export function RestaurantList({
	navigation,
	visibleRestaurants,
	showEstimatedDeliveryFee = false,
}) {
	const { isDelivery, isTestMode, deliveryEstimate, cart } = useAppSelector(
		state => state.persisted
	)
	const { loadedData } = useAppSelector(state => state.data)
	const {
		allowOrdersOutsideOrgHours,
		localTimezone,
		deliveryEnabled,
		webOrdersEnabled,

		appDisplayOptions,
	} = useAppSelector(state => state.settings)
	const { randomizeRestaurantDisplay: randomizeRestaurantList } = appDisplayOptions
	const { logSelectMenuAnalytics } = useFirebaseAnalyticsContext()
	const hasItems = cart.length > 0
	const noAddress = deliveryEstimate.estimate.timeToDeliver == -1
	const numColumns = LARGE_LIST_WIDTH ? 3 : MED_LIST_WIDTH && !SMALL_LIST_WIDTH ? 2 : 1
	const MINUTES_BEFORE_CLOSE = 20

	const [showNoRestaurantsText, setShowText] = useState(false)
	const [displayRestaurants, setDisplayRestaurants] = useState([])
	const { shadowContainer, cardContainer } = useGlobalStyles()
	const { isMobile } = useBreakpoints()

	const timeHoursDifference = useGetTimeHoursDifference()

	useEffect(() => {
		if (randomizeRestaurantList) {
			// Create a copy and shuffle it
			console.log('radomizing restaurants')
			const shuffled = [...visibleRestaurants].sort(() => Math.random() - 0.5)
			setDisplayRestaurants(shuffled)
		} else {
			console.log('not radomizing restaurants')
			setDisplayRestaurants(visibleRestaurants)
		}
	}, [visibleRestaurants, randomizeRestaurantList])

	useEffect(() => {
		//slight delay before showing text
		if (visibleRestaurants.length <= 0) {
			const timer = setTimeout(() => setShowText(true), 150)
			return () => clearTimeout(timer)
		} else {
			setShowText(false)
		}
	}, [visibleRestaurants])

	if (
		visibleRestaurants.length <= 0 ||
		(Platform.OS === 'web' && !webOrdersEnabled && !isTestMode)
	) {
		return (
			<>
				{showNoRestaurantsText ? (
					<Text
						style={{
							textAlign: 'center',
							fontStyle: 'italic',
							color: Colors.greyscale[7],
							fontSize: 16, // Replace with your `largeTextSize` variable
							paddingTop: Platform.OS === 'web' ? 20 : 40,
						}}
					>
						{'We are unable to accept new orders at this time.'}
					</Text>
				) : (
					<></>
				)}
			</>
		)
	}

	const renderItem = (restaurant, index) => {
		const isLastInRow = (index + 1) % numColumns === 0
		const isFirstInRow = index % numColumns === 0
		const isSingleItemInRow =
			numColumns > 1 && index === visibleRestaurants.length - 1 && index % numColumns !== 0
		// const deliveryEstimate = deliveryEstimateList.find(
		// 	deliveryEstimate => deliveryEstimate.restaurantId === restaurant.id
		// )
		let fee = -1
		let deliveryText = ''
		if (!noAddress && hasItems && deliveryEstimate && isDelivery && deliveryEnabled) {
			let minTime = Math.round((deliveryEstimate.estimate.timeToDeliver * 0.7) / 5) * 5 + 5 //round to nearest 5 minutes
			const maxTime = deliveryEstimate.estimate.timeToDeliver + 5
			if (minTime === maxTime) {
				minTime -= 5
			}

			fee = deliveryEstimate.estimate.fee <= 0 ? 0 : deliveryEstimate.estimate.fee / 100
			deliveryText = `${minTime}-${maxTime} min.`
			if (showEstimatedDeliveryFee) {
				deliveryText += ` ${Formatter.currencyFormat(fee)}`
			}
		}

		const isOpen = isRestaurantOpen(
			restaurant,
			loadedData[global.org].hours ?? null,
			allowOrdersOutsideOrgHours,
			localTimezone
		)

		const isDeliveryUnavailable = !deliveryEstimate && isDelivery && deliveryEnabled
		const isUnableToAcceptOrders = isDeliveryUnavailable || !isOpen

		return (
			<View
				key={`restaurant-container-${index}-${restaurant.id}`}
				style={[
					{
						width: `${100 / numColumns}%`,
						marginVertical: smallTextSize,
					},
					isFirstInRow
						? { paddingRight: smallTextSize }
						: isLastInRow
							? { paddingLeft: smallTextSize }
							: { paddingHorizontal: smallTextSize / 2 },
					isSingleItemInRow && {
						marginRight: (numColumns - (index % numColumns) - 1) * (smallTextSize / 2),
					},
				]}
			>
				<TouchableOpacity
					key={`restaurant-touchable-${restaurant.id}`}
					disabled={isDeliveryUnavailable || (!isOpen && global.kiosk && !global.pos)}
					onPress={() => {
						if (isDeliveryUnavailable) {
							console.log('Delivery unavailable')
						} else if (restaurant !== undefined) {
							logSelectMenuAnalytics('brand_menu', restaurant.id) // log analytics
							//setRestaurant(dispatch, restaurant)
							if (restaurant.slug) {
								navigation.navigate('MenuBySlug', { slug: restaurant.slug })
							} else {
								navigation.navigate('Menu', { rId: restaurant.id })
							}
						} else {
							console.log('Could not load.')
						}
					}}
				>
					<View
						style={[
							shadowContainer,
							{
								opacity: !isOpen || isDeliveryUnavailable ? 0.5 : 1,
								...(Platform.OS === 'web' && {
									filter: !isOpen || isDeliveryUnavailable ? 'grayscale(80%)' : 'none',
									opacity: !isOpen || isDeliveryUnavailable ? 0.6 : 1,
								}),
							},
						]}
					>
						<View
							style={[cardContainer, { opacity: !isOpen && global.kiosk && !global.pos ? 0.6 : 1 }]}
						>
							{restaurant?.heroImg ? (
								<ImageObject
									source={restaurant.heroImg}
									style={{
										height: Platform.OS === 'web' ? 200 : 170,
										opacity: !isOpen || isDeliveryUnavailable ? 0.8 : 1,
									}}
								/>
							) : (
								<View
									style={{
										height: Platform.OS === 'web' ? 200 : 170,
										backgroundColor: colors.grayTranslucent,
									}}
								></View>
							)}
							{/* Show greyed-out overlay message if store closed or delivery unavailable */}
							{isUnableToAcceptOrders ? (
								<View
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: '100%',
										height: Platform.OS === 'web' ? 200 : 170,
										backgroundColor: Colors.greyscale[4],
										justifyContent: 'center',
										alignItems: 'center',
										opacity: 0,
									}}
								></View>
							) : null}
							<View style={[styles.textArea]}>
								{restaurant?.description ? (
									<Stack gap="sm">
										<Inline gap="sm" alignY="center">
											<Text numberOfLines={1} ellipsizeMode="tail" style={styles.name}>
												{restaurant.name}
											</Text>
											<Description
												isOpen={isOpen}
												restaurant={restaurant}
												orgHours={loadedData[global.org].hours ?? null}
												allowOrdersOutsideOrgHours={allowOrdersOutsideOrgHours}
												localTimezone={localTimezone}
											/>
										</Inline>

										<DSText
											level={isMobile ? 'xsmall' : 'small'}
											tone="secondary"
											weight="medium"
											numberOfLines={1}
										>
											{restaurant.description}
										</DSText>
									</Stack>
								) : (
									<Stack gap="sm">
										<Text numberOfLines={1} ellipsizeMode="tail" style={styles.name}>
											{restaurant.name}
										</Text>

										<Description
											// indefiniteSnooze={indefiniteSnooze}
											// isSnoozed={isSnoozed}
											// minutesToClose={minutesToClose}
											isOpen={isOpen}
											restaurant={restaurant}
											orgHours={loadedData[global.org].hours ?? null}
											allowOrdersOutsideOrgHours={allowOrdersOutsideOrgHours}
											localTimezone={localTimezone}
										/>
									</Stack>
								)}
							</View>
						</View>
					</View>
				</TouchableOpacity>
			</View>
		)
	}
	function openTomorrowOrToday(nextOpeningTime: number) {
		const today = new Date()
		if (today.getHours() > nextOpeningTime) {
			return ' tomorrow '
		}
		return ' '
	}
	function descriptionSnooze({ restaurant }) {
		if (restaurant.snoozeUntil === null) {
			return { message: `Not Taking orders right now`, descriptionStyle: styles.descriptionClosed }
		}
		return {
			message: `Closed until ${restaurant.snoozeUntil}`,
			descriptionStyle: styles.descriptionClosed,
		}
	}

	function descriptionMapper({
		isOpen,
		restaurant,
		orgHours,
		allowOrdersOutsideOrgHours,
		localTimezone,
		minutesToClose,
	}) {
		// const isSnoozed = checkSnooze(restaurant.isSnoozed, restaurant.snoozeUntil)

		const restaurantHours = getHoursStatus(
			restaurant,
			orgHours,
			allowOrdersOutsideOrgHours,
			localTimezone,
			timeHoursDifference
		)
		if (!isOpen) {
			if (restaurant.isSnoozed) {
				if (restaurant.snoozeUntil === -1) {
					return {
						message: `Not taking orders right now`,
						descriptionStyle: styles.descriptionClosed,
						icon: <MaterialCommunityIcons name="cancel" style={[styles.descriptionClosed]} />,
					}
				}
				if (restaurant.snoozeUntil > 0) {
					const snoozeUntilHours = epochToHours(restaurant.snoozeUntil)
					return {
						message: `Closed until${openTomorrowOrToday(snoozeUntilHours)}${formatTimeWithoutPadding(snoozeUntilHours)}`,
						descriptionStyle: styles.descriptionClosed,
						icon: <MaterialCommunityIcons name="cancel" style={[styles.descriptionClosed]} />,
					}
				}
			}
			return {
				message: `Closed until${openTomorrowOrToday(restaurantHours.nextOpeningTime)}${formatTimeWithoutPadding(restaurantHours.nextOpeningTime)}`,
				descriptionStyle: styles.descriptionClosed,
				icon: <MaterialCommunityIcons name="cancel" style={[styles.descriptionClosed]} />,
			}
		}
		if (minutesToClose !== null) {
			if (minutesToClose > MINUTES_BEFORE_CLOSE) {
				return {
					message: `Open until ${formatTimeWithoutPadding(restaurantHours.closingTime)}`,
					descriptionStyle: styles.descriptionOpen,
					icon: undefined,
				}
			}
			if (minutesToClose <= MINUTES_BEFORE_CLOSE) {
				return {
					message: `Closing in ${(minutesToClose as number).toFixed()} minutes`,
					descriptionStyle: styles.descriptionClosing,
					icon: undefined,
				}
			}
		}
		return {
			message: `Open 24/7`,
			descriptionStyle: styles.descriptionOpen,
			icon: undefined,
		}
	}

	function Description({
		isOpen,
		restaurant,
		orgHours,
		allowOrdersOutsideOrgHours,
		localTimezone,
	}) {
		const [descriptionTimestamp, setDescriptionTimestamp] = useState(null)
		useEffect(() => {
			const interval = setInterval(() => {
				setDescriptionTimestamp(new Date().getTime())
			}, 60000)
			return () => clearInterval(interval)
		}, [])
		const description = useMemo(() => {
			return descriptionMapper({
				isOpen,
				restaurant,
				orgHours,
				allowOrdersOutsideOrgHours,
				localTimezone,
				minutesToClose: minutesUntilClose(
					restaurant,
					orgHours,
					allowOrdersOutsideOrgHours,
					localTimezone
				),
			})
		}, [
			isOpen,
			descriptionTimestamp,
			restaurant,
			orgHours,
			allowOrdersOutsideOrgHours,
			localTimezone,
		])

		return (
			<View>
				<Text numberOfLines={1} ellipsizeMode="tail" style={[description?.descriptionStyle]}>
					{description?.icon} {description?.message}
				</Text>
			</View>
		)
	}

	return (
		<View
			style={{
				flexDirection: 'row',
				flexWrap: 'wrap',
				marginHorizontal: Dimensions.get('window').width * 0.04,
				//marginRight: Dimensions.get('window').width * 0.04,
				marginTop: smallTextSize,
				justifyContent: 'flex-start',
				...(numColumns <= 1 && {
					marginRight: Dimensions.get('window').width * 0.04 - 12.5,
				}),
			}}
		>
			{displayRestaurants.map((restaurant, index) => renderItem(restaurant, index))}
		</View>
	)
}

const styles = StyleSheet.create({
	name: {
		fontSize: smallTextSize,
		fontWeight: '600',
		// minHeight: sTextSize * 1.75,
	},
	description: {
		fontSize: sTextSize,
		color: Colors.greyscale[5],
		// minHeight: sTextSize * 1.25,
	},
	descriptionOpen: {
		fontSize: sTextSize,
		color: Colors.custom.mediumGreen,
		// minHeight: sTextSize * 1.25,
	},
	descriptionClosing: {
		fontSize: sTextSize,
		color: Colors.custom.pinkRed,
		// minHeight: sTextSize * 1.25,
	},
	descriptionClosed: {
		fontSize: sTextSize,
		color: 'black',
		// minHeight: sTextSize * 1.25,
	},
	textArea: {
		padding: sTextSize,
	},
})
